import React from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  TextField,
  Typography,
} from '@mui/material'
import { Block as BlockIcon } from '@mui/icons-material'
import {
  StyledButton,
  StyledDialog,
  StyledDialogContent,
  StyledDialogTitle,
} from '../../styles/StyledComponents'
import type { CompanyWithStats } from '../../types'

interface SuspendCompanyDialogProps {
  open: boolean
  company: CompanyWithStats | null
  reason: string
  onReasonChange: (reason: string) => void
  submitting: boolean
  onClose: () => void
  onConfirm: () => void
}

/**
 * Confirmation for suspending a company. Suspension is enforced server-side and cloud-side: it
 * blocks room joins, the lobby and the Matrix chat token, disconnects live sessions immediately,
 * limits members to the "Company Account Disabled" screen (plus My Profile, What's new and Leave
 * Feedback), freezes meeting bookings and their reminders without cancelling any, and stops
 * invitation links. Memberships and billing are untouched. The admin must see all of that before
 * anything is sent. Nothing here calls the network; the parent does that on confirm.
 */
export const SuspendCompanyDialog: React.FC<SuspendCompanyDialogProps> = ({
  open,
  company,
  reason,
  onReasonChange,
  submitting,
  onClose,
  onConfirm,
}) => {
  const name = company?.name || 'this company'
  const memberCount = typeof company?.total_users === 'number' ? company.total_users : null

  return (
    <StyledDialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>
        <BlockIcon sx={{ color: 'warning.main', fontSize: 24 }} />
        Suspend company: {company?.name}
      </StyledDialogTitle>
      <StyledDialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {memberCount !== null
            ? `This affects ${memberCount} ${memberCount === 1 ? 'member' : 'members'} of ${name}.`
            : `This affects every member of ${name}.`}
        </Alert>
        <Typography variant="body2" sx={{ color: 'text.primary', mb: 1 }}>
          While the company is suspended:
        </Typography>
        <Box
          component="ul"
          data-testid="suspend-company-effects"
          sx={{ m: 0, mb: 2, pl: 3, color: 'text.primary', '& li': { mb: 0.5 } }}
        >
          <li>
            <Typography variant="body2">
              Members won&apos;t be able to enter the company&apos;s rooms or lobby, and Matrix chat
              will stop.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">Anyone in those rooms now will be disconnected.</Typography>
          </li>
          <li>
            <Typography variant="body2">
              Members can still sign in, but they&apos;ll see a &quot;Company Account Disabled&quot;
              screen. Only My Profile, What&apos;s new and Leave Feedback stay available.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Meetings can&apos;t be booked, changed or answered, and reminders pause. No meeting is
              cancelled.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Invitation links stop working until you reactivate.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">Memberships and billing are unchanged.</Typography>
          </li>
          <li>
            <Typography variant="body2">You can undo this at any time with Reactivate.</Typography>
          </li>
        </Box>
        <TextField
          margin="dense"
          label="Reason for suspension (optional)"
          fullWidth
          multiline
          rows={2}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          disabled={submitting}
          inputProps={{ 'data-testid': 'suspend-company-reason' }}
        />
      </StyledDialogContent>
      <DialogActions
        sx={{ px: 3, py: 2, bgcolor: 'background.default', borderTop: 1, borderColor: 'divider' }}
      >
        <StyledButton onClick={onClose} variant="outlined" disabled={submitting}>
          Cancel
        </StyledButton>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="warning"
          disabled={submitting}
          data-testid="suspend-company-confirm"
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <BlockIcon />}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
        >
          {submitting ? 'Suspending...' : 'Suspend company'}
        </Button>
      </DialogActions>
    </StyledDialog>
  )
}
